import { IFormInputMessage } from '@grubhubprod/gh-cookbook-react';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import {
  getIndexes,
  valueType,
} from 'src/components/oneCorp/features/MealCredit/common/formFields/Validation/useGroupFieldsValidate/useGroupFieldsValidate.utils';

export type FormFieldValue = boolean | string | number;

export interface UseGroupFieldsValidate {
  validate: (value: FormFieldValue, groupLength: number) => boolean | string;
  error: IFormInputMessage;
}

export interface UseGroupFieldsValidateProps {
  name: string;
  errorMessage: string;
}

export const useGroupFieldsValidate = ({
  name,
  errorMessage,
}: UseGroupFieldsValidateProps): UseGroupFieldsValidate => {
  const { trigger, errors, getValues, clearErrors, formState } =
    useFormContext();
  const isDirty = !!formState.dirtyFields[name];
  const hasError = !!errors[name];
  const error = errors[name]?.find((e: IFormInputMessage) => e);

  const triggerValidationForGroup = useCallback(
    (values: FormFieldValue[]) => {
      const indexes = getIndexes(values);
      indexes.forEach((i: number) => {
        trigger(`${name}[${i}]`);
      });
    },
    [trigger, name]
  );

  useEffect(() => {
    // Ensures validation is triggered every time the dependency array changes:
    // - When any form field in the group becomes dirty (`isDirty`), this effect triggers validation for all form fields
    //   in the group to ensure they are properly validated as a whole.
    // - If there's an error (`hasError`) associated with any form field in the group, it also re-triggers validation
    //   for the entire group.

    const values = getValues()[name] as FormFieldValue[];

    // Trigger validation for the group only if the group is dirty
    // or contains at least one truthy value.
    if (isDirty || hasError || values?.some((value) => value)) {
      triggerValidationForGroup(values);
    }
  }, [trigger, isDirty, name, getValues, hasError, triggerValidationForGroup]);

  function validate(value: valueType, groupLength: number): boolean | string {
    const values = getValues()[name] || [];

    const allDaysUnselected =
      // Ensure that the validation logic only checks for "all days unselected" when all form fields
      // have been rendered (i.e., the number of rendered fields matches the expected group length).
      // This prevents premature validation during form initialization
      // The check ensures validation only occurs once all fields are fully rendered
      groupLength === values.length &&
      values.every((isSelected: boolean) => !isSelected);
    if (value) {
      clearErrors(name);
    }

    return !value && allDaysUnselected ? errorMessage : true;
  }

  return {
    validate,
    error,
  };
};
